'use client';
import { faAngleLeft, faAngleRight, faAnglesLeft, faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
interface PaginationProps {
    data?: {
        page_no?: number;
        per_page?: number;
        start_record?: number;
        end_record?: number;
        total_records?: number;
        filteredRecords?: number;
    };
    setPerpage: (value: number) => void;
    setPageNumber: (value: number) => void;
    isLoading?: boolean;
}
const Pagination = ({ data, setPerpage, setPageNumber, isLoading = false }: PaginationProps) => {
    if (!data) data = { page_no: 1, per_page: 25, start_record: 1, end_record: 1, total_records: 1, filteredRecords: 1 };

    const { page_no, per_page, start_record, end_record, total_records, filteredRecords } = data;

    const total_no_records = useMemo(() => filteredRecords ?? total_records, [filteredRecords, total_records]);

    const total_pages = useMemo(
        () => (total_no_records && per_page ? Math.ceil(total_no_records / per_page) : 0),
        [total_no_records, per_page]
    );

    const [renderPages, setRenderPages] = useState<number[]>([]);

    const [selectedPerPage, setSelectedPerPage] = useState(per_page);

    const [selectedPageNumber, setSelectedPageNumber] = useState(page_no ?? 1);

    const handleCalculateRenderPages = useCallback(() => {
        const total = total_pages;
        const delta = selectedPageNumber === 1 || selectedPageNumber === total ? 2 : 1;

        const range: number[] = [];
        const left = Math.max(2, selectedPageNumber - delta);
        const right = Math.min(total - 1, selectedPageNumber + delta);

        // Always show first page
        range.push(1);

        if (left > 2) {
            range.push(-1); // represent '...'
        }

        for (let i = left; i <= right; i++) {
            range.push(i);
        }

        if (right < total - 1) {
            range.push(-2); // represent '...'
        }

        if (total > 1) {
            range.push(total);
        }

        setRenderPages(range);
    }, [selectedPageNumber, total_pages]);

    const handleChangePerPage = useCallback(
        (per_page: number) => {
            setPerpage(per_page);
            setSelectedPerPage(per_page);
            setSelectedPageNumber(1);
        },
        [setPerpage]
    );

    const handleChangePageNumber = useCallback(
        (page_no: number) => {
            if (page_no < 1 || page_no > total_pages || page_no === selectedPageNumber) {
                return;
            }

            setPageNumber(page_no);
            setSelectedPageNumber(page_no);
        },
        [total_pages, selectedPageNumber, setPageNumber]
    );

    useEffect(() => {
        if (total_pages > 0) {
            handleCalculateRenderPages();
        }
    }, [handleCalculateRenderPages, total_pages]);

    useEffect(() => {
        if (page_no) setSelectedPageNumber(page_no);
        if (per_page) setSelectedPerPage(per_page);
    }, [page_no, per_page]);

    return (
        <div className={`pagination-component row my-lg-3 align-items-center me-lg-2`} style={{ fontSize: 13 }}>
            <div className="col-12 col-lg-4 d-flex align-items-center justify-content-center justify-content-lg-start">
                <span className="text-muted text-sm">
                    Showing results {start_record ?? 0} - {end_record ?? 0} of {filteredRecords ?? total_records ?? 0}{' '}
                </span>
            </div>
            <div
                className={`my-1 my-sm-auto text-sm col-12 col-lg-3 d-flex align-items-center justify-content-center justify-content-lg-end ${
                    isLoading ? 'opacity-75 pe-none' : ''
                }`}
            >
                <span>Per page</span>
                <select
                    value={selectedPerPage}
                    className="border-0 outline-0"
                    onChange={(e) => handleChangePerPage(parseInt(e.target.value))}
                >
                    <option value="20">20</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
            </div>
            <div
                className={`col-12 col-lg-5 d-flex justify-content-center justify-content-lg-end gap-2 align-items-center navigation-buttons-container ${
                    isLoading ? 'opacity-75 pe-none' : ''
                }`}
            >
                <span
                    className={`${selectedPageNumber === 1 ? 'disabled' : 'cursor-pointer material-shadow'}`}
                    style={{ minWidth: 28, textAlign: 'center' }}
                    onClick={() => handleChangePageNumber(Math.max(1, selectedPageNumber - 10))}
                    aria-label="Go 10 pages back"
                    title="Go 10 pages back"
                >
                    <FontAwesomeIcon size="xs" icon={faAnglesLeft} />
                </span>

                <span
                    className={`${selectedPageNumber === 1 ? 'disabled' : 'cursor-pointer material-shadow'}`}
                    style={{ minWidth: 28, textAlign: 'center' }}
                    onClick={() => handleChangePageNumber(selectedPageNumber - 1)}
                    aria-label="Go one page back"
                    title="Go one page back"
                >
                    <FontAwesomeIcon size="xs" icon={faAngleLeft} />
                </span>

                {renderPages.map((page, idx) => {
                    if (page === -1 || page === -2) {
                        return (
                            <span className="disabled mb-1" key={`dots-${idx}`}>
                                ...
                            </span>
                        );
                    }

                    return (
                        <span
                            className={`material-shadow ${selectedPageNumber === page ? 'active user-select-none' : 'cursor-pointer'}`}
                            style={{ minWidth: 28, textAlign: 'center' }}
                            key={page}
                            onClick={() => handleChangePageNumber(page)}
                            aria-label={`Go to page ${page}`}
                            title={`Go to page ${page}`}
                        >
                            {page}
                        </span>
                    );
                })}

                <span
                    className={`${selectedPageNumber === total_pages ? 'disabled' : 'cursor-pointer material-shadow'}`}
                    style={{ minWidth: 28, textAlign: 'center' }}
                    onClick={() => handleChangePageNumber(selectedPageNumber + 1)}
                    aria-label="Go one page forward"
                    title="Go one page forward"
                >
                    <FontAwesomeIcon size="xs" icon={faAngleRight} />
                </span>

                <span
                    className={`${selectedPageNumber === total_pages ? 'disabled pe-none' : 'cursor-pointer material-shadow'}`}
                    style={{ minWidth: 28, textAlign: 'center' }}
                    onClick={() => handleChangePageNumber(Math.min(total_pages, selectedPageNumber + 10))}
                    aria-label="Go 10 pages forward"
                    title="Go 10 pages forward"
                >
                    <FontAwesomeIcon size="xs" icon={faAnglesRight} />
                </span>
            </div>
        </div>
    );
};

export default memo(Pagination);
